/**
 * Frota · Manutenção · Checklists — painel do módulo + lista/histórico.
 *
 * O resumo e a lista ficam na mesma tela de propósito: quem abre o módulo quer
 * saber o que está pendente e agir, não navegar entre um painel e uma listagem.
 *
 * O filtro por placa é o que responde "histórico do veículo" — a URL aceita
 * `?plate=XXX`, então dá para chegar aqui de qualquer lugar que conheça a placa.
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  MaintenanceRunDto,
  MaintenanceSummaryDto,
  MaintenanceTemplateSummaryDto,
  MaintenanceVehicleContextDto,
} from "@bi/types";
import { api, ApiError } from "../lib/api";
import { useFilters, periodToDates } from "../lib/filters";
import { fmtNumber } from "../lib/format";
import { PageHeader } from "../components/PageHeader";
import { KPIStatCard } from "../components/KPIStatCard";
import { EmptyState } from "../components/EmptyState";
import { IconWrench, IconTruck } from "../components/icons";
import { KIND_LABEL, MOVEMENT_LABEL, RUN_STATUS } from "../features/maintenance/labels";

export function MaintenanceChecklistsPage() {
  const [params, setParams] = useSearchParams();
  const { filters } = useFilters();
  const { from, to } = periodToDates(filters.period, filters.customFrom, filters.customTo);

  const plate = params.get("plate") ?? "";
  const status = params.get("status") ?? "";
  const movimento = params.get("movimento") ?? "";
  const tipo = params.get("tipo") ?? "";
  const motorista = params.get("motorista") ?? "";
  const de = params.get("de") ?? "";
  const ate = params.get("ate") ?? "";
  const [novoAberto, setNovoAberto] = useState(false);

  const resumo = useQuery({
    queryKey: ["maintenance", "summary", from, to],
    queryFn: () => api.get<MaintenanceSummaryDto>(`/maintenance/summary?from=${from}&to=${to}`),
    staleTime: 30_000,
  });

  const lista = useQuery({
    queryKey: ["maintenance", "runs", plate, status, movimento, tipo, motorista, de, ate],
    queryFn: () => {
      const q = new URLSearchParams();
      if (plate) q.set("plate", plate);
      if (status) q.set("status", status);
      if (movimento) q.set("movement", movimento);
      if (tipo) q.set("kind", tipo);
      if (motorista) q.set("driverId", motorista);
      if (de) q.set("from", `${de}T00:00:00`);
      if (ate) q.set("to", `${ate}T23:59:59`);
      q.set("limit", "100");
      return api.get<{ rows: MaintenanceRunDto[]; total: number }>(`/maintenance/runs?${q}`);
    },
  });

  const setParam = (chave: string, valor: string) => {
    const p = new URLSearchParams(params);
    if (valor) p.set(chave, valor);
    else p.delete(chave);
    setParams(p, { replace: true });
  };

  const s = resumo.data;
  const motoristas = useQuery({
    queryKey: ["maintenance", "drivers"],
    queryFn: () => api.get<{ items: Array<{ id: string; name: string }> }>("/maintenance/drivers?q="),
    staleTime: 5 * 60_000,
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Frota · Manutenção · Checklists"
        subtitle="Entrega e devolução de veículo, não conformidades e histórico por placa"
        actions={
          <button className="btn-primary" onClick={() => setNovoAberto(true)}>
            Novo checklist
          </button>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <KPIStatCard label="Em andamento" memory={{ cardKey: "manutencao.checklists-pendentes", from, to, expected: s?.runsInProgress }} value={s ? fmtNumber(s.runsInProgress) : "—"}
          comparisonLabel="checklists abertos"
          status={s && s.runsInProgress > 0 ? "warning" : "neutral"} loading={resumo.isLoading} />
        <KPIStatCard label="Concluídos no período" memory={{ cardKey: "manutencao.checklists-concluidos", from, to, expected: s?.runsCompletedInPeriod }} value={s ? fmtNumber(s.runsCompletedInPeriod) : "—"}
          comparisonLabel="no período do filtro" status="neutral" loading={resumo.isLoading} />
        <KPIStatCard label="Não conformidades abertas" memory={{ cardKey: "manutencao.nc-abertas", expected: s?.nonConformitiesOpen }} value={s ? fmtNumber(s.nonConformitiesOpen) : "—"}
          comparisonLabel="abertas ou em análise"
          status={s && s.nonConformitiesOpen > 0 ? "warning" : "good"} loading={resumo.isLoading} />
        <KPIStatCard label="Ocorrências críticas" memory={{ cardKey: "manutencao.nc-criticas", expected: s?.nonConformitiesCritical }} value={s ? fmtNumber(s.nonConformitiesCritical) : "—"}
          comparisonLabel="criticidade alta"
          status={s && s.nonConformitiesCritical > 0 ? "critical" : "good"} loading={resumo.isLoading} />
        <KPIStatCard label="O.S. abertas" memory={{ cardKey: "manutencao.os-ativas", expected: s?.workOrdersOpen }} value={s ? fmtNumber(s.workOrdersOpen) : "—"}
          comparisonLabel="ordens em aberto"
          status={s && s.workOrdersOpen > 0 ? "warning" : "neutral"} loading={resumo.isLoading} />
      </div>

      {/* Filtros */}
      <div className="card p-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-500">Placa</span>
          <input
            className="input w-40 uppercase"
            placeholder="RYR9G63"
            defaultValue={plate}
            onBlur={(e) => setParam("plate", e.target.value.trim().toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && setParam("plate", (e.target as HTMLInputElement).value.trim().toUpperCase())}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-500">Situação</span>
          <select className="input w-44" value={status} onChange={(e) => setParam("status", e.target.value)}>
            <option value="">Todas</option>
            <option value="draft">Rascunho</option>
            <option value="in_progress">Em andamento</option>
            <option value="completed">Concluído</option>
            <option value="cancelled">Cancelado</option>
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-500">Saída / retorno</span>
          <select className="input w-40" value={movimento} onChange={(e) => setParam("movimento", e.target.value)}>
            <option value="">Todos</option>
            <option value="departure">Saída</option>
            <option value="arrival">Retorno</option>
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-500">Tipo</span>
          <select className="input w-40" value={tipo} onChange={(e) => setParam("tipo", e.target.value)}>
            <option value="">Todos</option>
            {Object.entries(KIND_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-500">Motorista</span>
          <select className="input w-48" value={motorista} onChange={(e) => setParam("motorista", e.target.value)}>
            <option value="">Todos</option>
            {(motoristas.data?.items ?? []).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-500">De</span>
          <input type="date" className="input w-40" value={de} onChange={(e) => setParam("de", e.target.value)} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-gray-500">Até</span>
          <input type="date" className="input w-40" value={ate} onChange={(e) => setParam("ate", e.target.value)} />
        </label>
        {(plate || status || movimento || tipo || motorista || de || ate) && (
          <button className="btn-secondary" onClick={() => setParams({}, { replace: true })}>
            Limpar filtros
          </button>
        )}
        <Link className="btn-secondary ml-auto" to="/frota/manutencao/nao-conformidades">
          Ver não conformidades
        </Link>
      </div>

      {/* Lista */}
      <div className="card overflow-hidden">
        {lista.isLoading ? (
          <div className="p-8 text-sm text-gray-500">Carregando…</div>
        ) : (lista.data?.rows.length ?? 0) === 0 ? (
          <EmptyState
            icon={<IconWrench className="w-6 h-6" />}
            title="Nenhum checklist encontrado"
            description={plate ? `Nada registrado para a placa ${plate}.` : "Comece iniciando um checklist de entrega ou devolução."}
            action={<button className="btn-primary" onClick={() => setNovoAberto(true)}>Novo checklist</button>}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-muted text-xs uppercase text-gray-500">
                <tr>
                  <th className="text-left px-4 py-2">Checklist</th>
                  <th className="text-left px-4 py-2">Placa</th>
                  <th className="text-left px-4 py-2">Motorista</th>
                  <th className="text-left px-4 py-2">Aplicado por</th>
                  <th className="text-left px-4 py-2">Início</th>
                  <th className="text-left px-4 py-2">Conclusão</th>
                  <th className="text-right px-4 py-2">KM</th>
                  <th className="text-right px-4 py-2">Respostas</th>
                  <th className="text-right px-4 py-2">NCs</th>
                  <th className="text-left px-4 py-2">Situação</th>
                </tr>
              </thead>
              <tbody>
                {lista.data!.rows.map((r) => (
                  <tr key={r.id} className="border-t border-gray-100 hover:bg-surface-muted">
                    <td className="px-4 py-2">
                      <Link className="text-tm-600 hover:underline font-medium" to={`/frota/manutencao/checklists/${r.id}`}>
                        {r.template?.name ?? KIND_LABEL[r.kind]}
                      </Link>
                      <div className="text-xs text-gray-400">
                        {KIND_LABEL[r.kind]}
                        {r.movement && r.kind !== "delivery" && r.kind !== "return_delivery" ? ` · ${MOVEMENT_LABEL[r.movement]}` : ""}
                      </div>
                    </td>
                    <td className="px-4 py-2 font-mono">
                      {r.vehiclePlate}
                      {r.trailerPlate && <span className="text-gray-400"> + {r.trailerPlate}</span>}
                    </td>
                    <td className="px-4 py-2">{r.driverName ?? "—"}</td>
                    <td className="px-4 py-2">{r.inspectorName ?? "—"}</td>
                    <td className="px-4 py-2">{new Date(r.startedAt).toLocaleString("pt-BR")}</td>
                    <td className="px-4 py-2">{r.finishedAt ? new Date(r.finishedAt).toLocaleString("pt-BR") : "—"}</td>
                    <td className="px-4 py-2 text-right">{r.odometerKm != null ? fmtNumber(r.odometerKm) : "—"}</td>
                    <td className="px-4 py-2 text-right text-gray-600">{r._count?.answers ?? 0}</td>
                    <td className={`px-4 py-2 text-right ${(r._count?.nonConformities ?? 0) > 0 ? "text-danger-600 font-medium" : ""}`}>{r._count?.nonConformities ?? 0}</td>
                    <td className="px-4 py-2"><span className={RUN_STATUS[r.status].badge}>{RUN_STATUS[r.status].label}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {novoAberto && <NovoChecklist onFechar={() => setNovoAberto(false)} />}
    </div>
  );
}

/**
 * Início do checklist.
 *
 * Aqui mora o compromisso do briefing de não pedir o que o SIAG já sabe: ao
 * escolher a placa, a tela busca o contexto do veículo e traz sozinha a carreta
 * vinculada e o último KM conhecido — com a FONTE à mostra, porque odômetro de
 * abastecimento é dígito humano e não pode ser apresentado como leitura.
 */
function NovoChecklist({ onFechar }: { onFechar: () => void }) {
  const qc = useQueryClient();
  const [templateId, setTemplateId] = useState("");
  const [placa, setPlaca] = useState("");
  const [placaConfirmada, setPlacaConfirmada] = useState("");
  const [carreta, setCarreta] = useState("");
  const [driverId, setDriverId] = useState("");
  const [movimento, setMovimento] = useState<"" | "departure" | "arrival">("");
  const [erro, setErro] = useState<string | null>(null);

  const templates = useQuery({
    queryKey: ["maintenance", "templates"],
    queryFn: () => api.get<{ items: MaintenanceTemplateSummaryDto[] }>("/maintenance/templates"),
  });

  const sugestoes = useQuery({
    queryKey: ["maintenance", "vehicles", placa],
    queryFn: () => api.get<{ items: Array<{ plate: string; isTrailer: boolean; vehicleTypeName: string | null }> }>(
      `/maintenance/vehicles?q=${encodeURIComponent(placa)}`,
    ),
    enabled: placa.length >= 2 && placa !== placaConfirmada,
  });

  const contexto = useQuery({
    queryKey: ["maintenance", "vehicle-context", placaConfirmada],
    queryFn: () => api.get<MaintenanceVehicleContextDto>(`/maintenance/vehicle-context?plate=${placaConfirmada}`),
    enabled: placaConfirmada.length >= 5,
    retry: false,
  });

  const motoristas = useQuery({
    queryKey: ["maintenance", "drivers"],
    // Só motoristas próprios (empregados) — o cadastro do ERP tem milhares de terceiros.
    queryFn: () => api.get<{ items: Array<{ id: string; name: string }> }>("/maintenance/drivers?q="),
  });

  // Carreta vinculada no cadastro entra sozinha — o usuário pode trocar.
  useEffect(() => {
    if (contexto.data?.trailerPlate) setCarreta(contexto.data.trailerPlate);
  }, [contexto.data?.trailerPlate]);

  const criar = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>("/maintenance/runs", {
        templateId,
        vehiclePlate: placaConfirmada,
        trailerPlate: carreta || null,
        driverId: driverId || null,
        movement: movimento || null,
      }),
    onSuccess: (run) => {
      qc.invalidateQueries({ queryKey: ["maintenance"] });
      window.location.assign(`/frota/manutencao/checklists/${run.id}`);
    },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível iniciar o checklist."),
  });

  const podeCriar = templateId && placaConfirmada && !criar.isPending;
  const ultimo = contexto.data?.lastOdometer;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-xl rounded-t-xl max-h-[92vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white">
          <h3 className="font-semibold text-gray-900">Novo checklist</h3>
          <button className="text-gray-400 text-xl leading-none" onClick={onFechar} aria-label="Fechar">×</button>
        </div>

        <div className="p-5 space-y-4">
          <label className="block">
            <span className="text-sm text-gray-600">Checklist</span>
            <select className="input w-full mt-1" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
              <option value="">Selecione…</option>
              {(templates.data?.items ?? [])
                // "Execução da O.S." controla serviço de uma O.S.; não é inspeção e não inicia por aqui
                .filter((t) => t.publishedVersion && t.kind !== "work_order")
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} (v{t.publishedVersion!.version} · {t.itemCount} itens)
                  </option>
                ))}
            </select>
          </label>

          {/* Entrega/devolução já dizem; Diário e Manutenção precisam (a API recusa sem). */}
          {(() => {
            const k = templates.data?.items.find((t) => t.id === templateId)?.kind;
            if (!k || k === "delivery" || k === "return_delivery") return null;
            return (
              <label className="block">
                <span className="text-sm text-gray-600">Saída ou retorno?</span>
                <select className="input w-full mt-1" value={movimento} onChange={(e) => setMovimento(e.target.value as typeof movimento)}>
                  <option value="">Selecione…</option>
                  <option value="departure">Saída</option>
                  <option value="arrival">Retorno (liga à última saída da placa)</option>
                </select>
              </label>
            );
          })()}

          <label className="block">
            <span className="text-sm text-gray-600">Placa do cavalo</span>
            <input
              className="input w-full mt-1 uppercase font-mono"
              placeholder="RYR9G63"
              value={placa}
              onChange={(e) => { setPlaca(e.target.value.toUpperCase()); setErro(null); }}
            />
          </label>

          {placa.length >= 2 && placa !== placaConfirmada && (sugestoes.data?.items.length ?? 0) > 0 && (
            <div className="border border-gray-200 rounded-lg divide-y max-h-48 overflow-y-auto">
              {sugestoes.data!.items.map((v) => (
                <button
                  key={v.plate}
                  className="w-full text-left px-3 py-2.5 hover:bg-surface-muted"
                  onClick={() => { setPlaca(v.plate); setPlacaConfirmada(v.plate); }}
                >
                  <span className="font-mono font-medium">{v.plate}</span>
                  <span className="text-xs text-gray-500 ml-2">
                    {v.vehicleTypeName ?? (v.isTrailer ? "Carreta" : "Cavalo")}
                  </span>
                </button>
              ))}
            </div>
          )}

          {contexto.isError && (
            <p className="text-sm text-danger-600">Placa fora do cadastro de veículos da empresa.</p>
          )}

          {contexto.data && (
            <div className="rounded-lg bg-surface-muted p-3 text-sm space-y-1">
              <div className="flex items-center gap-2 text-gray-700">
                <IconTruck className="w-4 h-4" />
                <span className="font-mono font-medium">{contexto.data.plate}</span>
                <span className="text-gray-500">{contexto.data.vehicleTypeName ?? ""}</span>
              </div>
              <div className="text-gray-600">
                Último KM conhecido:{" "}
                {ultimo ? (
                  <>
                    <strong>{fmtNumber(ultimo.km)} km</strong>{" "}
                    <span className="text-gray-400">
                      ({ultimo.sourceLabel}
                      {ultimo.readAt ? ` · ${new Date(ultimo.readAt).toLocaleDateString("pt-BR")}` : ""})
                    </span>
                  </>
                ) : (
                  <span className="text-gray-400">sem leitura registrada</span>
                )}
              </div>
            </div>
          )}

          <label className="block">
            <span className="text-sm text-gray-600">Placa do implemento</span>
            <input
              className="input w-full mt-1 uppercase font-mono"
              placeholder="opcional"
              value={carreta}
              onChange={(e) => setCarreta(e.target.value.toUpperCase())}
            />
          </label>

          <label className="block">
            <span className="text-sm text-gray-600">Motorista</span>
            <select className="input w-full mt-1" value={driverId} onChange={(e) => setDriverId(e.target.value)}>
              <option value="">Não informado</option>
              {(motoristas.data?.items ?? []).map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </label>

          {erro && <p className="text-sm text-danger-600">{erro}</p>}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 flex gap-2 sticky bottom-0 bg-white">
          <button className="btn-secondary flex-1" onClick={onFechar}>Cancelar</button>
          <button className="btn-primary flex-1" disabled={!podeCriar} onClick={() => criar.mutate()}>
            {criar.isPending ? "Iniciando…" : "Iniciar checklist"}
          </button>
        </div>
      </div>
    </div>
  );
}
